<template>
  <div class="legal-document">
    <PrePeersInstitutionalPageHero
      :title="props.title"
      :highlighted-title-part="props.highlightedTitlePart">
      <p
        v-if="props.versionLabel"
        class="badge badge-blue legal-document__version">
        <PrePeersIcon
          name="calendar"
          class="legal-document__version-icon" />
        {{ props.versionLabel }}
      </p>
    </PrePeersInstitutionalPageHero>

    <div
      class="legal-document__container legal-document__body"
      :class="{ 'legal-document__body--without-summary': !props.hasSummary }">
      <nav
        v-if="props.hasSummary"
        class="legal-document__summary"
        :aria-label="$t('legalPages.document.summaryAriaLabel')">
        <details
          ref="summaryPanelElement"
          class="legal-document__summary-panel">
          <summary
            class="legal-document__summary-toggle"
            @click="keepSummaryOpenOnDesktop">
            {{ $t('legalPages.document.summaryTitle') }}
            <span class="legal-document__summary-toggle-icon">
              <PrePeersIcon name="chevron-down" />
            </span>
          </summary>
          <ol class="legal-document__summary-list">
            <li
              v-for="item in summaryItems"
              :key="item.id">
              <a
                :href="`#${item.id}`"
                class="legal-document__summary-link"
                :class="{
                  'legal-document__summary-link--active': item.id === highlightedSummaryArticleId,
                }"
                :aria-current="item.id === highlightedSummaryArticleId ? 'true' : undefined"
                @click="closeSummaryOnMobile">
                <span class="legal-document__summary-number">{{ item.number ?? '—' }}</span>
                <span>{{ item.title }}</span>
              </a>
            </li>
          </ol>
        </details>
      </nav>

      <div
        ref="contentElement"
        class="legal-document__content"
        v-html="props.html" />

      <div
        v-if="$slots.footer"
        class="legal-document__footer">
        <slot name="footer" />
      </div>
    </div>

    <button
      type="button"
      class="legal-document__back-to-top"
      :class="{ 'legal-document__back-to-top--visible': isBackToTopVisible }"
      :aria-label="$t('legalPages.document.backToTop')"
      @click="scrollToTop">
      <PrePeersIcon name="arrow-up" />
    </button>
  </div>
</template>

<script lang="ts" setup>
import type { ComputedRef, Ref } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import PrePeersIcon from '~/components/icons/PrePeersIcon.vue'
import PrePeersInstitutionalPageHero from '~/components/sections/PrePeersInstitutionalPageHero.vue'
import type {
  PrePeersLegalDocumentProps,
  PrePeersLegalDocumentSummaryItem,
} from '~/types/PrePeersLegalDocument'

const DESKTOP_LAYOUT_MEDIA_QUERY: string = '(min-width: 1025px)'
const ARTICLE_SELECTOR: string = '.legal-document__article'
const ARTICLE_NUMBER_SELECTOR: string = '.legal-document__article-number'
const SPACES_AND_LINE_BREAKS_PATTERN: RegExp = /\s+/g
const READING_LINE_FROM_TOP_PX: number = 120
const BACK_TO_TOP_VISIBLE_AFTER_PX: number = 700

const props: PrePeersLegalDocumentProps = defineProps({
  title: {
    type: String,
    required: true,
  },
  highlightedTitlePart: {
    type: String,
    default: '',
  },
  versionLabel: {
    type: String,
    default: '',
  },
  html: {
    type: String,
    required: true,
  },
  hasSummary: {
    type: Boolean,
    default: true,
  },
})

const contentElement: Ref<HTMLElement | null> = ref(null)
const summaryPanelElement: Ref<HTMLDetailsElement | null> = ref(null)
const summaryItems: Ref<PrePeersLegalDocumentSummaryItem[]> = ref([])
const articleIdBeingRead: Ref<string> = ref('')
const isBackToTopVisible: Ref<boolean> = ref(false)

let desktopLayoutMediaQuery: MediaQueryList | null = null

const highlightedSummaryArticleId: ComputedRef<string> = computed(
  (): string => articleIdBeingRead.value || (summaryItems.value[0]?.id ?? ''),
)

/**
 * Liste les articles du texte affiché pour construire le sommaire.
 * @param {HTMLElement} documentElement - Conteneur du texte juridique.
 * @returns {PrePeersLegalDocumentSummaryItem[]} Ancre, numéro et titre de chaque article.
 */
function buildSummaryItems(documentElement: HTMLElement): PrePeersLegalDocumentSummaryItem[] {
  const articles: HTMLElement[] = Array.from(
    documentElement.querySelectorAll<HTMLElement>(ARTICLE_SELECTOR),
  )

  return articles.map((article: HTMLElement): PrePeersLegalDocumentSummaryItem => {
    const fullTitle: string = article.querySelector('h2')?.textContent ?? ''
    const articleNumber: string = article.querySelector(ARTICLE_NUMBER_SELECTOR)?.textContent ?? ''

    return {
      id: article.id,
      number: articleNumber ? articleNumber.replace('Article', '').trim() : null,
      title: fullTitle
        .replace(articleNumber, '')
        .replace(SPACES_AND_LINE_BREAKS_PATTERN, ' ')
        .trim(),
    }
  })
}

/**
 * Article en cours de lecture : le dernier dont le titre est passé sous le header.
 * @param {HTMLElement} documentElement - Conteneur du texte juridique.
 * @returns {string} Ancre de l'article lu.
 */
function findArticleBeingRead(documentElement: HTMLElement): string {
  const articles: HTMLElement[] = Array.from(
    documentElement.querySelectorAll<HTMLElement>(ARTICLE_SELECTOR),
  )
  const isAtBottomOfPage: boolean =
    Math.ceil(window.innerHeight + window.scrollY) >= document.documentElement.scrollHeight

  if (isAtBottomOfPage) return articles[articles.length - 1]?.id ?? ''

  let articleBeingRead: string = articles[0]?.id ?? ''
  for (const article of articles) {
    if (article.getBoundingClientRect().top <= READING_LINE_FROM_TOP_PX) {
      articleBeingRead = article.id
    }
  }

  return articleBeingRead
}

/**
 * Met à jour l'entrée surlignée du sommaire et l'affichage du bouton « retour en haut ».
 * @returns {void}
 */
function updateReadingPosition(): void {
  if (!contentElement.value) return

  articleIdBeingRead.value = findArticleBeingRead(contentElement.value)
  isBackToTopVisible.value = window.scrollY > BACK_TO_TOP_VISIBLE_AFTER_PX
}

/**
 * Ouvre le sommaire sur desktop et le replie sur mobile.
 * @returns {void}
 */
function openSummaryOnDesktopOnly(): void {
  if (!summaryPanelElement.value) return

  summaryPanelElement.value.open = desktopLayoutMediaQuery?.matches ?? false
}

/**
 * Empêche de replier le sommaire sur desktop.
 * @param {MouseEvent} event - Clic sur l'intitulé « Sommaire ».
 * @returns {void}
 */
function keepSummaryOpenOnDesktop(event: MouseEvent): void {
  if (desktopLayoutMediaQuery?.matches) event.preventDefault()
}

/**
 * Replie le sommaire sur mobile avant que le lien n'amène à l'article.
 * @returns {void}
 */
function closeSummaryOnMobile(): void {
  if (!summaryPanelElement.value || desktopLayoutMediaQuery?.matches) return

  summaryPanelElement.value.open = false
}

/**
 * Remonte en haut de la page.
 * @returns {void}
 */
function scrollToTop(): void {
  window.scrollTo({ top: 0 })
}

onMounted((): void => {
  desktopLayoutMediaQuery = window.matchMedia(DESKTOP_LAYOUT_MEDIA_QUERY)
  openSummaryOnDesktopOnly()
  desktopLayoutMediaQuery.addEventListener('change', openSummaryOnDesktopOnly)

  if (contentElement.value) summaryItems.value = buildSummaryItems(contentElement.value)
  updateReadingPosition()
  window.addEventListener('scroll', updateReadingPosition, { passive: true })
})

onBeforeUnmount((): void => {
  desktopLayoutMediaQuery?.removeEventListener('change', openSummaryOnDesktopOnly)
  window.removeEventListener('scroll', updateReadingPosition)
})
</script>

<style scoped>
.legal-document__container {
  width: 100%;
  max-width: min(var(--container-lg), calc(100% - 2 * var(--space-6)));
  margin-inline: auto;
}

.legal-document__version {
  gap: var(--space-2);
  padding: 0.35em 0.85em;
}

.legal-document__version-icon {
  width: 14px;
  height: 14px;
}

.legal-document__body {
  display: grid;
  grid-template-columns: var(--sidebar-width) minmax(0, 1fr);
  gap: var(--space-12);
  align-items: start;
  padding-block: var(--space-10) var(--space-16);
}

.legal-document__body--without-summary {
  grid-template-columns: minmax(0, 1fr);
}

.legal-document__summary {
  position: sticky;
  top: calc(var(--header-height) + var(--space-6));
}

.legal-document__summary-panel {
  max-height: calc(100vh - var(--header-height) - var(--space-12));
  overflow-y: auto;
  padding: var(--space-4);
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  scrollbar-width: thin;
  scrollbar-color: var(--border-default) transparent;
}

.legal-document__summary-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-1) var(--space-2) var(--space-3);
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  color: var(--text-muted);
  list-style: none;
  cursor: default;
}

.legal-document__summary-toggle::-webkit-details-marker {
  display: none;
}

.legal-document__summary-toggle-icon {
  display: none;
  transition: transform var(--duration-base) var(--ease-out);
}

.legal-document__summary-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.legal-document__summary-link {
  display: grid;
  grid-template-columns: 1.75rem minmax(0, 1fr);
  gap: var(--space-1);
  padding: var(--space-2);
  border-left: 2px solid transparent;
  border-radius: 0 var(--radius-md) var(--radius-md) 0;
  font-size: var(--text-sm);
  line-height: var(--leading-snug);
  color: var(--text-secondary);
  transition:
    color var(--duration-fast) var(--ease-out),
    background var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out);
}

.legal-document__summary-number {
  font-weight: 700;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}

.legal-document__summary-link:hover {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.04);
}

.legal-document__summary-link--active {
  color: var(--accent-blue-lt);
  background: rgba(3, 160, 241, 0.1);
  border-left-color: var(--accent-blue);
}

.legal-document__summary-link--active .legal-document__summary-number {
  color: var(--accent-blue-lt);
}

.legal-document__footer {
  display: flex;
  grid-column: -2 / -1;
}

.legal-document__content {
  min-width: 0;
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
}

.legal-document__content :deep(.legal-document__article) {
  padding-block: var(--space-10);
  border-top: 1px solid var(--border-subtle);
  scroll-margin-top: calc(var(--header-height) + var(--space-6));
}

.legal-document__content :deep(.legal-document__article:first-child) {
  padding-top: 0;
  border-top: 0;
}

.legal-document__content :deep(.legal-document__article:last-child) {
  padding-bottom: 0;
}

.legal-document__content :deep(.legal-document__article-heading) {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-bottom: var(--space-6);
  font-size: var(--text-2xl);
  font-weight: 700;
  color: var(--text-primary);
}

.legal-document__content :deep(.legal-document__article-number) {
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  color: var(--accent-blue-lt);
}

.legal-document__content :deep(p) {
  margin-bottom: var(--space-4);
  text-wrap: pretty;
}

.legal-document__content :deep(p:last-child) {
  margin-bottom: 0;
}

.legal-document__content :deep(strong) {
  font-weight: 600;
  color: var(--text-primary);
}

.legal-document__content :deep(a) {
  background: var(--gradient-blue);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  font-weight: 600;
  word-break: break-word;
}

.legal-document__content :deep(a:hover),
.legal-document__content :deep(a:focus-visible) {
  text-decoration: underline;
  text-decoration-color: var(--accent-blue);
  text-underline-offset: 3px;
}

.legal-document__content :deep(.legal-document__subheading) {
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
  margin: var(--space-8) 0 var(--space-3);
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text-primary);
  scroll-margin-top: calc(var(--header-height) + var(--space-6));
}

.legal-document__content :deep(.legal-document__article-heading + .legal-document__subheading) {
  margin-top: 0;
}

.legal-document__content :deep(.legal-document__subheading-number) {
  font-size: var(--text-base);
  font-weight: 700;
  color: var(--accent-blue-lt);
  font-variant-numeric: tabular-nums;
}

.legal-document__content :deep(.legal-document__clause-list) {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.legal-document__content :deep(.legal-document__clause) {
  display: grid;
  grid-template-columns: 3rem minmax(0, 1fr);
  gap: var(--space-2);
  scroll-margin-top: calc(var(--header-height) + var(--space-6));
}

.legal-document__content :deep(.legal-document__clause-number) {
  font-weight: 700;
  color: var(--accent-blue-lt);
  font-variant-numeric: tabular-nums;
}

.legal-document__content :deep(.legal-document__clause-text) {
  min-width: 0;
}

.legal-document__content :deep(.legal-document__bullet-list) {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin: var(--space-2) 0 var(--space-5);
}

.legal-document__content :deep(.legal-document__bullet-list:last-child) {
  margin-bottom: 0;
}

.legal-document__content :deep(.legal-document__bullet-list li) {
  position: relative;
  padding-left: var(--space-5);
}

.legal-document__content :deep(.legal-document__bullet-list li::before) {
  content: '';
  position: absolute;
  top: calc(0.85em - 4px);
  left: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--accent-orange);
}

.legal-document__content :deep(.legal-document__table-wrapper) {
  margin: var(--space-2) 0;
  overflow-x: auto;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.legal-document__content :deep(.legal-document__table) {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
}

.legal-document__content :deep(.legal-document__table th) {
  padding: var(--space-3) var(--space-4);
  text-align: left;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  color: var(--text-muted);
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid var(--border-subtle);
}

.legal-document__content :deep(.legal-document__table th:nth-child(1)) {
  width: 30%;
}

.legal-document__content :deep(.legal-document__table th:nth-child(2)) {
  width: 22%;
}

.legal-document__content :deep(.legal-document__table th:nth-child(3)) {
  width: 26%;
}

.legal-document__content :deep(.legal-document__table th:nth-child(4)) {
  width: 22%;
}

.legal-document__content :deep(.legal-document__table td) {
  padding: var(--space-3) var(--space-4);
  vertical-align: top;
  color: var(--text-secondary);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  overflow-wrap: break-word;
}

.legal-document__content :deep(.legal-document__table tbody tr:first-child td) {
  border-top: 0;
}

.legal-document__content :deep(.legal-document__table td:first-child) {
  font-weight: 600;
  color: var(--text-primary);
}

.legal-document__back-to-top {
  position: fixed;
  right: var(--space-6);
  bottom: var(--space-6);
  z-index: 400;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-full);
  background: var(--bg-elevated);
  border: 1px solid var(--border-default);
  color: var(--accent-blue-lt);
  box-shadow: var(--shadow-md);
  cursor: pointer;
  opacity: 0;
  transform: translateY(8px);
  pointer-events: none;
  transition:
    opacity var(--duration-base) var(--ease-out),
    transform var(--duration-base) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out);
}

.legal-document__back-to-top--visible {
  opacity: 1;
  transform: none;
  pointer-events: auto;
}

.legal-document__back-to-top:hover,
.legal-document__back-to-top:focus-visible {
  border-color: var(--accent-blue-lt);
  box-shadow: var(--shadow-md), var(--shadow-glow-sm);
}

@media (max-width: 1024px) {
  .legal-document__body {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-8);
  }

  .legal-document__summary {
    position: static;
  }

  .legal-document__summary-panel {
    max-height: none;
    padding: var(--space-2) var(--space-4);
  }

  .legal-document__summary-toggle {
    padding: var(--space-3) 0;
    color: var(--text-secondary);
    cursor: pointer;
  }

  .legal-document__summary-toggle-icon {
    display: inline-flex;
  }

  .legal-document__summary-panel[open] .legal-document__summary-toggle-icon {
    transform: rotate(180deg);
  }

  .legal-document__summary-list {
    padding-bottom: var(--space-3);
  }
}

@media (max-width: 768px) {
  .legal-document__body {
    padding-block: var(--space-6) var(--space-12);
  }

  .legal-document__content :deep(.legal-document__article) {
    padding-block: var(--space-8);
  }

  .legal-document__content :deep(.legal-document__article-heading) {
    font-size: var(--text-xl);
  }

  .legal-document__content :deep(.legal-document__clause) {
    grid-template-columns: 2.5rem minmax(0, 1fr);
  }

  .legal-document__content :deep(.legal-document__table-wrapper) {
    overflow: visible;
  }

  .legal-document__content :deep(.legal-document__table) {
    table-layout: auto;
  }

  .legal-document__content :deep(.legal-document__table thead) {
    display: none;
  }

  .legal-document__content :deep(.legal-document__table),
  .legal-document__content :deep(.legal-document__table tbody),
  .legal-document__content :deep(.legal-document__table tr),
  .legal-document__content :deep(.legal-document__table td) {
    display: block;
    width: 100%;
  }

  .legal-document__content :deep(.legal-document__table tr) {
    padding: var(--space-3) var(--space-4);
    border-top: 1px solid var(--border-subtle);
  }

  .legal-document__content :deep(.legal-document__table tr:first-child) {
    border-top: 0;
  }

  .legal-document__content :deep(.legal-document__table td) {
    padding: 2px 0;
    border: 0;
  }

  .legal-document__content :deep(.legal-document__table td:first-child) {
    padding-bottom: var(--space-2);
  }

  .legal-document__content :deep(.legal-document__table td:not(:first-child)::before) {
    content: attr(data-label) ' : ';
    font-weight: 600;
    color: var(--text-muted);
  }
}

@media (prefers-reduced-motion: reduce) {
  .legal-document__summary-link,
  .legal-document__summary-toggle-icon,
  .legal-document__back-to-top {
    transition: none;
  }
}

@media print {
  .legal-document__summary,
  .legal-document__back-to-top {
    display: none;
  }

  .legal-document__body {
    display: block;
  }
}
</style>
